import { useEffect, useRef, useState } from 'react';

/** Lerp-smoothed window.scrollY for fluid ship / parallax motion */
export const useSmoothScroll = () => {
  const [y, setY] = useState(0);
  const target = useRef(0);

  useEffect(() => {
    const onScroll = () => {
      target.current = window.scrollY;
    };
    let frame = 0;

    const tick = () => {
      setY((prev) => {
        const diff = target.current - prev;
        if (Math.abs(diff) < 0.4) return target.current;
        return prev + diff * 0.1;
      });
      frame = requestAnimationFrame(tick);
    };

    onScroll();
    frame = requestAnimationFrame(tick);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return y;
};

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** 0 → 1 progress while scrolling through a tall sticky section */
export const useSectionScrollProgress = (
  sectionRef: React.RefObject<HTMLElement | null>,
  scrollY: number,
) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const measure = () => {
      const start = section.offsetTop;
      const scrollRange = Math.max(1, section.offsetHeight - window.innerHeight);
      setProgress(clamp((scrollY - start) / scrollRange, 0, 1));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [scrollY, sectionRef]);

  return progress;
};

export const waveBob = (y: number, phase = 0, amplitude = 5) =>
  Math.sin(y * 0.012 + phase) * amplitude;
